import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';

export const ServiceStatus: React.FC = () => {
  const [status, setStatus] = useState<null | {
    api: { status: string; time: string };
    db: {
      status: string;
      pathResolved: string;
      counts: { plans: number; features: number; tiers?: number };
    };
    customerPortal: { status: string; url: string; code?: number };
    stripe: { status: string; sample?: string };
  }>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const resp = await api.getServiceStatus();
        if (resp.success) {
          setStatus(resp.data);
        } else {
          setError('Failed to fetch status');
        }
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, []);

  const badge = (state?: string) => {
    const color =
      state === 'ok'
        ? 'bg-green-100 text-green-800'
        : state === 'down'
          ? 'bg-red-100 text-red-800'
          : 'bg-yellow-100 text-yellow-800';
    const text = state || 'unknown';
    return <span className={`px-2 py-1 rounded text-xs font-medium ${color}`}>{text}</span>;
  };

  if (loading) return <div className="p-6">Loading status...</div>;
  if (error) return <div className="p-6 text-red-600">{error}</div>;
  if (!status) return <div className="p-6">No status available</div>;

  return (
    <div className="space-y-6">
      <div className="bg-base-100 shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold text-base-content mb-2">Service Status</h1>
        <p className="text-base-content/70">Live overview of system components</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-base-100 shadow rounded-lg p-4">
          <h2 className="text-lg font-semibold text-base-content flex items-center justify-between">
            API
            {badge('ok')}
          </h2>
          <p className="text-sm text-base-content/70 mt-2">
            Time: {new Date(status.api.time).toLocaleString()}
          </p>
        </div>

        <div className="bg-base-100 shadow rounded-lg p-4">
          <h2 className="text-lg font-semibold text-base-content flex items-center justify-between">
            Database
            {badge(status.db.status)}
          </h2>
          <p className="text-sm text-base-content/70 mt-2 break-all">
            Path: {status.db.pathResolved}
          </p>
          <p className="text-sm text-base-content/70 mt-1">
            Plans: {status.db.counts.plans}, Features: {status.db.counts.features}, Tiers:{' '}
            {status.db.counts.tiers}
          </p>
        </div>

        <div className="bg-base-100 shadow rounded-lg p-4">
          <h2 className="text-lg font-semibold text-base-content flex items-center justify-between">
            Customer Portal
            {badge(status.customerPortal.status)}
          </h2>
          <p className="text-sm text-base-content/70 mt-2">URL: {status.customerPortal.url}</p>
          {status.customerPortal.code && (
            <p className="text-sm text-base-content/70 mt-1">HTTP: {status.customerPortal.code}</p>
          )}
        </div>

        <div className="bg-base-100 shadow rounded-lg p-4">
          <h2 className="text-lg font-semibold text-base-content flex items-center justify-between">
            Stripe
            {badge(status.stripe.status)}
          </h2>
          {status.stripe.sample && (
            <p className="text-sm text-base-content/70 mt-2">Sample: {status.stripe.sample}</p>
          )}
        </div>
      </div>

      <div className="bg-base-100 shadow rounded-lg p-4">
        <h3 className="text-md font-semibold text-base-content mb-2">Other stats</h3>
        <ul className="text-sm text-base-content list-disc ml-5 space-y-1">
          <li>Node env: {import.meta.env.MODE || 'development'}</li>
          <li>API base: {import.meta.env.VITE_API_URL || 'http://localhost:3001'}</li>
        </ul>
      </div>
    </div>
  );
};
